import { Box, Button, Form, HStack, SubmitButton } from '@medely/ui-kit';
import React from 'react';
import { CancellationForm } from './CancellationForm';
import { useGraphQLQuery } from '@mp/hooks/useGraphQLRequest';
import { ResultOf } from '@graphql-typed-document-node/core';
import { ContentLoader } from '@mp/components/ContentLoader';
import { CancellationReasonsGql } from '../cancellationQueries';
import { cancellationFormSchema } from './cancellationFormSchema';
import { FormValuesProps, UtmParamsType } from '../interface';
import { useCancelShiftButton } from '@mp/graphql/useCancelShiftButton';
import { organizedOptions } from '../cancelationUtils';
import { FragmentType, parseFragmentData } from '@mp/graphql/generated';
import { JobDetailsFragment } from '../../graphql';

type CancellationFormContainerProps = {
  closeDialog: () => void;
  utmParams?: UtmParamsType;
  job?: FragmentType<typeof JobDetailsFragment> | null;
};

export const CancellationFormContainer = ({
  closeDialog,
  utmParams,
  job: inputJob,
}: CancellationFormContainerProps) => {
  const job = parseFragmentData(JobDetailsFragment, inputJob);
  const isAssignment = !!job?.assignment;
  const { cancel, isLoading: isCancelLoading } = useCancelShiftButton({
    isAssignment,
    job: inputJob,
    utmParams,
    onSuccess: () => {
      closeDialog();
    },
  });
  const { data, isLoading } = useGraphQLQuery<ResultOf<typeof CancellationReasonsGql>>({
    operationName: 'GetCancellationReasonsForCancelationPolicy',
    query: CancellationReasonsGql,
    variables: {
      input: {
        filter: {
          role: 1,
        },
      },
    },
  });

  const cancellationReasons = data?.cancellationReasons || [];

  const options = cancellationReasons.map((c) => ({ value: c.id, label: c.label as string }));
  const otherId = options.find((o) => o.label.toLocaleLowerCase() === 'other')?.value || 15;

  const handleSubmit = (values: FormValuesProps) => {
    cancel(values);
  };

  return (
    <ContentLoader loading={isLoading}>
      <Form handleSubmit={handleSubmit} schema={cancellationFormSchema(otherId)}>
        <CancellationForm options={organizedOptions(options)} />
        <HStack gap={2}>
          <Box flex={1}>
            <Button
              onClick={closeDialog}
              variant="outlined"
              testId="cancel-shift-dialog-back-button"
              fullWidth
            >
              Back
            </Button>
          </Box>
          <Box flex={1}>
            <SubmitButton
              testId="cancel-shift-dialog-cancel-button"
              loading={isCancelLoading}
              fullWidth
            >
              Cancel shift
            </SubmitButton>
          </Box>
        </HStack>
      </Form>
    </ContentLoader>
  );
};
